// Prestaties: kleine doelen naast het bezorgen, elk met een beloning in euro's.
// Pure logica: BezorgerGame geeft de huidige stand mee en krijgt de nieuw behaalde prestaties terug.
import { t, type MessageKey } from '../i18n';
import type { SaveState } from '../state/save';
import type { Delivered } from './shift';

export interface ShiftFacts {
  deliveries: number;
  bestCombo: number;
  dayStars: number;
  fined: boolean;
  delivered: Delivered[];
  /** Alle orders uit één lading bezorgd, met een volle bak van minstens 3 */
  fullLoadDelivered: boolean;
  ended: boolean;
  daily: boolean;
}

export interface AchievementDef {
  id: string;
  icon: string;
  name: string;
  description: string;
  reward: number;
  check: (save: SaveState, shift: ShiftFacts | null) => boolean;
}

const delivered = (shift: ShiftFacts | null) => shift?.delivered ?? [];

export const ACHIEVEMENTS: readonly AchievementDef[] = [
  {
    id: 'eerste_levering',
    icon: '🥐',
    get name() { return t('ach.eerste_levering.name' as MessageKey); },
    get description() { return t('ach.eerste_levering.desc' as MessageKey); },
    reward: 2,
    check: (s) => s.deliveredCount >= 1,
  },
  {
    id: 'perfecte_taart',
    icon: '🎂',
    get name() { return t('ach.perfecte_taart.name' as MessageKey); },
    get description() { return t('ach.perfecte_taart.desc' as MessageKey); },
    reward: 5,
    check: (_s, sh) => delivered(sh).some((d) => d.order.product.sensitivity >= 2 && d.order.integrity >= 100 && d.result.stars === 5),
  },
  {
    id: 'combo_5',
    icon: '🔥',
    get name() { return t('ach.combo_5.name' as MessageKey); },
    get description() { return t('ach.combo_5.desc' as MessageKey); },
    reward: 6,
    check: (_s, sh) => (sh?.bestCombo ?? 0) >= 5,
  },
  {
    id: 'tien_op_een_dag',
    icon: '📦',
    get name() { return t('ach.tien_op_een_dag.name' as MessageKey); },
    get description() { return t('ach.tien_op_een_dag.desc' as MessageKey); },
    reward: 8,
    check: (_s, sh) => (sh?.deliveries ?? 0) >= 10,
  },
  {
    id: 'volle_bak',
    icon: '🧺',
    get name() { return t('ach.volle_bak.name' as MessageKey); },
    get description() { return t('ach.volle_bak.desc' as MessageKey); },
    reward: 6,
    check: (_s, sh) => !!sh?.fullLoadDelivered,
  },
  {
    id: 'bruidstaart',
    icon: '💒',
    get name() { return t('ach.bruidstaart.name' as MessageKey); },
    get description() { return t('ach.bruidstaart.desc' as MessageKey); },
    reward: 10,
    check: (_s, sh) => delivered(sh).some((d) => d.order.product.fillsBox && d.result.stars >= 4),
  },
  {
    id: 'sjors_verslagen',
    icon: '🏁',
    get name() { return t('ach.sjors_verslagen.name' as MessageKey); },
    get description() { return t('ach.sjors_verslagen.desc' as MessageKey); },
    reward: 8,
    check: (s) => s.stats.raceWins >= 3,
  },
  {
    id: 'topdag',
    icon: '⭐',
    get name() { return t('ach.topdag.name' as MessageKey); },
    get description() { return t('ach.topdag.desc' as MessageKey); },
    reward: 8,
    check: (_s, sh) => !!sh?.ended && sh.dayStars >= 3,
  },
  {
    id: 'foutloos',
    icon: '🧤',
    get name() { return t('ach.foutloos.name' as MessageKey); },
    get description() { return t('ach.foutloos.desc' as MessageKey); },
    reward: 8,
    check: (_s, sh) => !!sh?.ended && !sh.fined && sh.deliveries >= 6 && sh.delivered.every((d) => d.order.integrity >= 100),
  },
  {
    id: 'schans',
    icon: '🛹',
    get name() { return t('ach.schans.name' as MessageKey); },
    get description() { return t('ach.schans.desc' as MessageKey); },
    reward: 5,
    check: (s) => s.stats.stunts >= 1,
  },
  {
    id: 'hele_stad',
    icon: '🗺️',
    get name() { return t('ach.hele_stad.name' as MessageKey); },
    get description() { return t('ach.hele_stad.desc' as MessageKey); },
    reward: 10,
    check: (s) => s.stats.districts.length >= 7,
  },
  {
    id: 'dagelijks',
    icon: '📅',
    get name() { return t('ach.dagelijks.name' as MessageKey); },
    get description() { return t('ach.dagelijks.desc' as MessageKey); },
    reward: 3,
    check: (_s, sh) => !!sh?.ended && sh.daily,
  },
];

/** Geeft de prestaties terug die nu behaald zijn maar nog niet in de spelstand staan. */
export function newlyEarned(save: SaveState, shift: ShiftFacts | null): AchievementDef[] {
  return ACHIEVEMENTS.filter((a) => !save.achievements.includes(a.id) && a.check(save, shift));
}
